import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useCreateProject } from '@/api/projects';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new project id once creation succeeds. */
  onCreated?: (projectId: string) => void;
  /** Optional suggested name to prefill the form with (user can edit/clear). */
  defaultName?: string;
}

export function CreateProjectDialog({
  open,
  onOpenChange,
  onCreated,
  defaultName = '',
}: CreateProjectDialogProps) {
  const { show } = useToast();
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useCreateProject({
    onSuccess: (project) => {
      show({ tone: 'success', title: 'Project created', description: project.name });
      onOpenChange(false);
      onCreated?.(project.id);
    },
    onError: () => {
      show({ tone: 'danger', title: 'Could not create project' });
    },
  });

  function reset() {
    setName(defaultName);
    setDescription('');
    setError(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Project name is required.');
      return;
    }
    setError(null);
    create.mutate({ name: trimmed, description: description.trim() || undefined });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Create project</DialogTitle>
          <DialogDescription>
            Group teams and resources under a shared project.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="create-project-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            <Alert tone="info" title="Project starts empty">
              After creating, the project has no resources yet. Attach domains,
              instances and other resources afterwards from the project's
              Resources tab.
            </Alert>
            <Field label="Project name" required error={error ?? undefined}>
              {(props) => (
                <Input
                  {...props}
                  autoFocus
                  placeholder="My project"
                  value={name}
                  invalid={!!error}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Description"
              description="Optional — what this project is for."
            >
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  placeholder="Production edge services for the APAC region…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              )}
            </Field>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-project-form"
            disabled={!name.trim() || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
